import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface MerchantProfile {
  id: string;
  type: 'fixed' | 'itinerant';
  activityType: string;
  location: {
    city: string;
    lat: number;
    lng: number;
  };
}

export interface DailyRecommendation {
  date: string;
  dayName: string;
  services: {
    period: 'Petit-déjeuner' | 'Déjeuner' | 'Dîner';
    time: string;
    weather: { icon: string; temp: number; condition: string };
    products: ProductRecommendation[];
    totalItems: number;
    totalValue: number;
    confidence: number;
  }[];
}

export interface EventRecommendation {
  event: {
    id: string;
    name: string;
    date: string;
    time: string;
    type: string;
    location: string;
    distance?: number;
    weather: { icon: string; temp: number; condition: string };
    expectedAffluence: 'low' | 'medium' | 'high';
    isRecurring: boolean;
    estimatedRevenue?: number;
  };
  products: ProductRecommendation[];
  totalItems: number;
  totalValue: number;
  confidence: number;
}

export interface OpportunityEvent {
  id: string;
  name: string;
  date: string;
  time: string;
  type: string;
  location: string;
  distance: number;
  driveTime: string;
  expectedAffluence: 'high' | 'medium' | 'low';
  weather: { icon: string; temp: number; condition: string };
  estimatedRevenue: number;
  competitorCount: number;
  isRecommended: boolean;
  potentialScore: number;
}

export interface Event {
  id: string;
  name: string;
  date: string;
  time: string;
  type: string;
  location: string;
  weather: {
    icon: string;
    temp: number;
    condition: string;
    coefficient: number;
  };
  expectedAffluence: 'low' | 'medium' | 'high';
  isRecurring: boolean;
}

export interface ProductFactor {
  name: string;
  impact: string;
  value: number;
}

export interface HistoricalEvent {
  date: string;
  prepared: number;
  sold: number;
  waste: number;
}

export interface ProductRecommendation {
  id: string;
  name: string;
  icon: string;
  category: string;
  baseQty: number;
  recommended: number;
  low: number;
  high: number;
  unit: string;
  price: number;
  confidence: number;
  factors: ProductFactor[];
  historicalAccuracy: number;
  lastEvents: HistoricalEvent[];
}

@Injectable({
  providedIn: 'root',
})
export class RecommendationsService {
  private apiUrl = '/api/recommendations';

  constructor(private http: HttpClient) {}

  getUpcomingEvents(): Observable<Event[]> {
    // return this.http.get<Event[]>(`${this.apiUrl}/events`);

    // Mock data
    return of([
      {
        id: 'evt-1',
        name: 'Service du Mardi Midi',
        date: '2026-01-07',
        time: '11:00 - 14:00',
        type: 'Déjeuner',
        location: 'Boutique principale',
        weather: {
          icon: 'sun',
          temp: 15,
          condition: 'Ensoleillé',
          coefficient: 1.2,
        },
        expectedAffluence: 'high',
        isRecurring: true,
      },
      {
        id: 'evt-2',
        name: 'Marché du Samedi',
        date: '2026-01-11',
        time: '08:00 - 13:00',
        type: 'market',
        location: 'Place du marché',
        weather: {
          icon: 'cloud',
          temp: 12,
          condition: 'Nuageux',
          coefficient: 1.0,
        },
        expectedAffluence: 'medium',
        isRecurring: true,
      },
      {
        id: 'evt-3',
        name: 'Festival Gastronomique',
        date: '2026-01-15',
        time: '10:00 - 18:00',
        type: 'festival',
        location: 'Parc municipal',
        weather: {
          icon: 'rain',
          temp: 8,
          condition: 'Pluvieux',
          coefficient: 0.7,
        },
        expectedAffluence: 'high',
        isRecurring: false,
      },
    ]);
  }

  getRecommendations(eventId: string): Observable<ProductRecommendation[]> {
    // return this.http.get<ProductRecommendation[]>(`${this.apiUrl}/event/${eventId}`);

    // Mock data
    return of([
      {
        id: 'prod-1',
        name: 'Baguette tradition',
        icon: '🥖',
        category: 'Pains',
        baseQty: 50,
        recommended: 60,
        low: 55,
        high: 70,
        unit: 'unité',
        price: 1.2,
        confidence: 92,
        factors: [
          { name: 'Météo ensoleillée', impact: '+20%', value: 1.2 },
          { name: 'Affluence élevée', impact: '+15%', value: 1.15 },
          { name: 'Jour de semaine', impact: '-5%', value: 0.95 },
          { name: 'Historique similaire', impact: 'Base 50', value: 1.0 },
        ],
        historicalAccuracy: 88,
        lastEvents: [
          { date: '2025-12-31', prepared: 58, sold: 56, waste: 2 },
          { date: '2025-12-24', prepared: 62, sold: 60, waste: 2 },
          { date: '2025-12-17', prepared: 55, sold: 54, waste: 1 },
        ],
      },
      {
        id: 'prod-2',
        name: 'Croissant',
        icon: '🥐',
        category: 'Viennoiseries',
        baseQty: 30,
        recommended: 42,
        low: 38,
        high: 48,
        unit: 'unité',
        price: 1.4,
        confidence: 85,
        factors: [
          { name: 'Météo ensoleillée', impact: '+20%', value: 1.2 },
          { name: 'Service midi', impact: '+25%', value: 1.25 },
          { name: 'Affluence élevée', impact: '+10%', value: 1.1 },
        ],
        historicalAccuracy: 82,
        lastEvents: [
          { date: '2025-12-31', prepared: 40, sold: 39, waste: 1 },
          { date: '2025-12-24', prepared: 45, sold: 42, waste: 3 },
          { date: '2025-12-17', prepared: 38, sold: 37, waste: 1 },
        ],
      },
      {
        id: 'prod-3',
        name: 'Pain au chocolat',
        icon: '🍫',
        category: 'Viennoiseries',
        baseQty: 25,
        recommended: 35,
        low: 32,
        high: 40,
        unit: 'unité',
        price: 1.5,
        confidence: 88,
        factors: [
          { name: 'Météo ensoleillée', impact: '+15%', value: 1.15 },
          { name: 'Affluence élevée', impact: '+20%', value: 1.2 },
          { name: 'Tendance croissante', impact: '+5%', value: 1.05 },
        ],
        historicalAccuracy: 90,
        lastEvents: [
          { date: '2025-12-31', prepared: 33, sold: 32, waste: 1 },
          { date: '2025-12-24', prepared: 36, sold: 35, waste: 1 },
          { date: '2025-12-17', prepared: 34, sold: 34, waste: 0 },
        ],
      },
    ]);
  }

  getMerchantProfile(): Observable<MerchantProfile> {
    const userJson = sessionStorage.getItem('user');
    if (userJson) {
      try {
        const user = JSON.parse(userJson);
        return of({
          id: user._id || user.id || '',
          type: user.merchantType || 'fixed',
          activityType: user.activityType || '',
          location: {
            city: user.locationCity || user.location?.city || '',
            lat: user.location?.coordinates?.lat || 0,
            lng: user.location?.coordinates?.lng || 0,
          },
        });
      } catch {
        // fallback
      }
    }
    return of({
      id: '',
      type: 'fixed',
      activityType: '',
      location: { city: '', lat: 0, lng: 0 },
    });
  }

  // Pour marchands FIXES : recommandations sur 7 jours (données réelles)
  getWeeklyRecommendations(): Observable<DailyRecommendation[]> {
    const dayNames = [
      'Dimanche',
      'Lundi',
      'Mardi',
      'Mercredi',
      'Jeudi',
      'Vendredi',
      'Samedi',
    ];

    // Générer 7 dates
    const dates: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }

    // Appeler l'API pour chaque jour
    const requests = dates.map((date) =>
      this.getFixedRecommendationsFromAPI(date).pipe(
        map((res) => {
          const dateObj = new Date(date);
          const day: DailyRecommendation = {
            date,
            dayName: dayNames[dateObj.getDay()],
            services: (res.recommendations || []).map((rec: any) => ({
              period: rec.serviceName as any,
              time: '',
              weather: { icon: 'cloud', temp: 0, condition: '' },
              products: (rec.products || []).map((p: any) => ({
                id: p.productId || p.id || '',
                name: p.productName || p.name || '',
                icon: '🍽️',
                category: p.category || '',
                baseQty: p.baseQuantity || 0,
                recommended: p.recommendedQuantity || p.recommended || 0,
                low: p.low || Math.round((p.recommendedQuantity || 0) * 0.85),
                high: p.high || Math.round((p.recommendedQuantity || 0) * 1.15),
                unit: p.unit || 'unité',
                price: p.price || 0,
                confidence: p.confidenceScore || p.confidence || 0,
                factors: p.basedOn
                  ? Object.entries(p.basedOn).map(([k, v]) => ({
                      name: k,
                      impact: '',
                      value: v as number,
                    }))
                  : p.factors || [],
                historicalAccuracy: p.historicalAccuracy || 0,
                lastEvents: p.lastEvents || [],
              })),
              totalItems: (rec.products || []).reduce(
                (s: number, p: any) =>
                  s + (p.recommendedQuantity || p.recommended || 0),
                0,
              ),
              totalValue: (rec.products || []).reduce(
                (s: number, p: any) =>
                  s +
                  (p.recommendedQuantity || p.recommended || 0) *
                    (p.price || 0),
                0,
              ),
              confidence:
                (rec.products || []).length > 0
                  ? Math.round(
                      (rec.products || []).reduce(
                        (s: number, p: any) =>
                          s + (p.confidenceScore || p.confidence || 0),
                        0,
                      ) / (rec.products || []).length,
                    )
                  : 0,
            })),
          };
          return day;
        }),
        catchError(() => {
          // Fallback: jour vide si l'API échoue
          const dateObj = new Date(date);
          return of({
            date,
            dayName: dayNames[dateObj.getDay()],
            services: [],
          } as DailyRecommendation);
        }),
      ),
    );

    return forkJoin(requests);
  }

  // Pour marchands ITINÉRANTS : recommandations par événement créé
  getEventRecommendations(): Observable<EventRecommendation[]> {
    // return this.http.get<EventRecommendation[]>(`${this.apiUrl}/events`);

    return of([
      {
        event: {
          id: 'evt-1',
          name: 'Marché de Cannes',
          date: '2026-01-10',
          time: '08:00 - 13:00',
          type: 'market',
          location: 'Cannes - Place du marché',
          distance: 35,
          weather: { icon: 'sun', temp: 16, condition: 'Ensoleillé' },
          expectedAffluence: 'high',
          isRecurring: true,
          estimatedRevenue: 450,
        },
        products: this.generateMockProducts({
          icon: 'sun',
          temp: 16,
          condition: 'Ensoleillé',
        }),
        totalItems: 180,
        totalValue: 320,
        confidence: 90,
      },
      {
        event: {
          id: 'evt-2',
          name: 'Festival Gastronomique Nice',
          date: '2026-01-15',
          time: '10:00 - 18:00',
          type: 'festival',
          location: 'Nice - Parc Phoenix',
          distance: 65,
          weather: { icon: 'cloud', temp: 14, condition: 'Nuageux' },
          expectedAffluence: 'high',
          isRecurring: false,
          estimatedRevenue: 680,
        },
        products: this.generateMockProducts({
          icon: 'cloud',
          temp: 14,
          condition: 'Nuageux',
        }),
        totalItems: 250,
        totalValue: 520,
        confidence: 85,
      },
    ]);
  }

  // Pour marchands ITINÉRANTS : opportunités à proximité
  getNearbyOpportunities(): Observable<OpportunityEvent[]> {
    // return this.http.get<OpportunityEvent[]>(`${this.apiUrl}/opportunities`);

    return of([
      {
        id: 'opp-1',
        name: 'Marché Artisanal Saint-Raphaël',
        date: '2026-01-12',
        time: '09:00 - 14:00',
        type: 'market',
        location: 'Saint-Raphaël - Port',
        distance: 15,
        driveTime: '20 min',
        expectedAffluence: 'medium',
        weather: { icon: 'sun', temp: 17, condition: 'Ensoleillé' },
        estimatedRevenue: 380,
        competitorCount: 8,
        isRecommended: true,
        potentialScore: 85,
      },
      {
        id: 'opp-2',
        name: 'Fête du Mimosa - Mandelieu',
        date: '2026-01-18',
        time: '10:00 - 19:00',
        type: 'festival',
        location: 'Mandelieu-la-Napoule',
        distance: 45,
        driveTime: '50 min',
        expectedAffluence: 'high',
        weather: { icon: 'sun', temp: 15, condition: 'Ensoleillé' },
        estimatedRevenue: 720,
        competitorCount: 15,
        isRecommended: true,
        potentialScore: 92,
      },
      {
        id: 'opp-3',
        name: 'Marché Nocturne Antibes',
        date: '2026-01-20',
        time: '18:00 - 23:00',
        type: 'market',
        location: 'Antibes - Vieux Port',
        distance: 55,
        driveTime: '1h 05',
        expectedAffluence: 'medium',
        weather: { icon: 'cloud', temp: 13, condition: 'Nuageux' },
        estimatedRevenue: 290,
        competitorCount: 12,
        isRecommended: false,
        potentialScore: 65,
      },
      {
        id: 'opp-4',
        name: 'Festival Street Food - Monaco',
        date: '2026-01-25',
        time: '11:00 - 22:00',
        type: 'festival',
        location: 'Monaco - Port Hercule',
        distance: 95,
        driveTime: '1h 30',
        expectedAffluence: 'high',
        weather: { icon: 'sun', temp: 16, condition: 'Ensoleillé' },
        estimatedRevenue: 950,
        competitorCount: 25,
        isRecommended: true,
        potentialScore: 88,
      },
    ]);
  }

  private generateMockProducts(weather: any): ProductRecommendation[] {
    return [
      {
        id: 'prod-1',
        name: 'Burger classique',
        icon: '🍔',
        category: 'Burgers',
        baseQty: 40,
        recommended: 55,
        low: 50,
        high: 65,
        unit: 'unité',
        price: 8.5,
        confidence: 90,
        factors: [
          { name: `Météo ${weather.condition}`, impact: '+25%', value: 1.25 },
          { name: 'Affluence élevée', impact: '+15%', value: 1.15 },
        ],
        historicalAccuracy: 87,
        lastEvents: [
          { date: '2025-12-28', prepared: 52, sold: 51, waste: 1 },
          { date: '2025-12-21', prepared: 58, sold: 55, waste: 3 },
        ],
      },
      {
        id: 'prod-2',
        name: 'Frites',
        icon: '🍟',
        category: 'Accompagnements',
        baseQty: 60,
        recommended: 70,
        low: 65,
        high: 80,
        unit: 'portion',
        price: 3.5,
        confidence: 88,
        factors: [
          { name: 'Météo', impact: '+20%', value: 1.2 },
          { name: 'Produit populaire', impact: '+10%', value: 1.1 },
        ],
        historicalAccuracy: 85,
        lastEvents: [
          { date: '2025-12-28', prepared: 68, sold: 67, waste: 1 },
          { date: '2025-12-21', prepared: 72, sold: 70, waste: 2 },
        ],
      },
    ];
  }

  validatePreparation(data: any): Observable<any> {
    // return this.http.post(`${this.apiUrl}/validate`, data);
    return of({ success: true });
  }

  validatePreparationOld(
    eventId: string,
    adjustments: { [key: string]: number },
  ): Observable<any> {
    // return this.http.post(`${this.apiUrl}/validate`, { eventId, adjustments });
    return of({ success: true, message: 'Préparation validée' });
  }

  createEventFromOpportunity(opportunityId: string): Observable<any> {
    // return this.http.post(`${this.apiUrl}/create-event`, { opportunityId });
    return of({ success: true, eventId: `evt-${Date.now()}` });
  }

  // ============================================================================
  // VRAIES MÉTHODES API - Appel au backend pour les recommandations calculées
  // ============================================================================

  /**
   * Récupérer les recommandations pour aujourd'hui (tous services)
   */
  getTodayRecommendationsFromAPI(): Observable<{
    success: boolean;
    date: string;
    services: {
      serviceId: string;
      serviceName: string;
      startTime: string;
      endTime: string;
      products: ProductRecommendation[];
    }[];
  }> {
    return this.http.get<any>(`${this.apiUrl}/today`);
  }

  /**
   * Récupérer les recommandations par produit pour une date et optionnellement un service
   */
  getProductRecommendationsFromAPI(
    date?: string,
    serviceId?: string,
    serviceName?: string,
  ): Observable<{
    success: boolean;
    date: string;
    serviceId?: string;
    recommendations: {
      [productId: string]: {
        quantity: number;
        confidence: number;
        byService?: { [serviceName: string]: number };
      };
    };
    source: string;
  }> {
    let params = new HttpParams();
    if (date) params = params.set('date', date);
    if (serviceId) params = params.set('serviceId', serviceId);
    if (serviceName) params = params.set('serviceName', serviceName);

    return this.http.get<any>(`${this.apiUrl}/products`, { params });
  }

  /**
   * Récupérer les recommandations pour un événement
   */
  getEventRecommendationsFromAPI(eventId: string): Observable<{
    success: boolean;
    eventId: string;
    products: ProductRecommendation[];
    generatedAt: string;
    modelVersion: string;
  }> {
    return this.http.get<any>(`${this.apiUrl}/event/${eventId}`);
  }

  /**
   * Récupérer les recommandations pour marchand fixe (date + service)
   */
  getFixedRecommendationsFromAPI(
    date: string,
    serviceId?: string,
    serviceName?: string,
  ): Observable<{
    success: boolean;
    date: string;
    serviceId?: string;
    serviceName?: string;
    products?: ProductRecommendation[];
    recommendations?: {
      serviceId: string;
      serviceName: string;
      products: ProductRecommendation[];
    }[];
  }> {
    let params = new HttpParams().set('date', date);
    if (serviceId) params = params.set('serviceId', serviceId);
    if (serviceName) params = params.set('serviceName', serviceName);

    return this.http.get<any>(`${this.apiUrl}/fixed`, { params });
  }
}
